import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ShieldCheck, WarningCircle, User, ForkKnife, ArrowRight } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OAuthAuthorize() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState("");
  const [authorizing, setAuthorizing] = useState(false);

  const clientId = params.get("client_id") || "";
  const redirectUri = params.get("redirect_uri") || "";
  const state = params.get("state") || "";
  const scope = params.get("scope") || "profile";

  const missingParams = useMemo(() => !clientId || !redirectUri, [clientId, redirectUri]);
  const autoFired = useRef(false);

  useEffect(() => {
    if (!loading && !user) {
      const returnTo = window.location.pathname + window.location.search;
      navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
    }
  }, [loading, user, navigate]);

  // Sessão ativa + parâmetros válidos → emite o code e redireciona automaticamente (SSO transparente)
  useEffect(() => {
    if (!loading && user && !missingParams && !autoFired.current) {
      autoFired.current = true;
      handleAuthorize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user, missingParams]);

  const handleAuthorize = async () => {
    setAuthorizing(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/oauth/authorize`, {
        client_id: clientId,
        redirect_uri: redirectUri,
        state,
        scope,
        response_type: params.get("response_type") || "code",
        code_challenge: params.get("code_challenge") || null,
        code_challenge_method: params.get("code_challenge_method") || null,
      });
      window.location.href = data.redirect_url;
    } catch (e) {
      console.error("OAuth authorize falhou:", e.response?.status, e.response?.data || e.message);
      const status = e.response?.status;
      setError(e.response?.data?.detail || `Falha ao autorizar (${status ? `HTTP ${status}` : "sem resposta do servidor"}). Tente novamente.`);
      setAuthorizing(false);
    }
  };

  const handleDeny = () => {
    if (redirectUri) {
      const sep = redirectUri.includes("?") ? "&" : "?";
      window.location.href = `${redirectUri}${sep}error=access_denied${state ? `&state=${encodeURIComponent(state)}` : ""}`;
    } else {
      navigate("/");
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <p className="text-sm text-zinc-400">Verificando sessão...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-zinc-200 p-8" data-testid="oauth-consent-card">
        <div className="flex items-center justify-center gap-4 mb-6">
          <img src="/atlas-core-icon.png" alt="Fawkes Atlas Core" className="w-14 h-14 rounded-full border border-zinc-200" />
          <ArrowRight size={20} className="text-zinc-300" />
          <div className="w-14 h-14 rounded-full bg-zinc-950 text-white flex items-center justify-center font-bold text-xs">S&R</div>
        </div>

        {missingParams ? (
          <div className="text-center" data-testid="oauth-error-invalid">
            <WarningCircle size={40} className="mx-auto text-rose-500 mb-3" />
            <h1 className="text-lg font-bold text-zinc-900 mb-1">Requisição OAuth inválida</h1>
            <p className="text-sm text-zinc-500">Parâmetros obrigatórios ausentes (client_id e redirect_uri). Verifique a configuração do Fawkes Atlas Core.</p>
            <Button variant="outline" className="mt-5" onClick={() => navigate("/")} data-testid="oauth-back-home">Voltar ao SOP & RAFT</Button>
          </div>
        ) : !error ? (
          <div className="text-center" data-testid="oauth-auto-redirect">
            <h1 className="text-lg font-bold text-zinc-900">Conectando ao Fawkes Atlas Core</h1>
            <p className="text-sm text-zinc-500 mt-1 mb-5">Sessão de {user.email} validada — redirecionando com segurança...</p>
            <div className="flex items-center justify-center gap-2 text-zinc-400">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
              </span>
              <span className="text-xs">Emitindo código de autorização</span>
            </div>
          </div>
        ) : (
          <>
            <h1 className="text-lg font-bold text-zinc-900 text-center">Fawkes Atlas Core</h1>
            <p className="text-sm text-zinc-500 text-center mt-1 mb-5">
              deseja acessar sua conta do SOP & RAFT
            </p>
            <div className="rounded-lg bg-zinc-50 border border-zinc-100 p-4 space-y-2.5 mb-5">
              <div className="flex items-center gap-2.5 text-sm text-zinc-700">
                <User size={16} className="text-zinc-400 shrink-0" />
                Seu perfil ({user.email})
              </div>
              <div className="flex items-center gap-2.5 text-sm text-zinc-700">
                <ForkKnife size={16} className="text-zinc-400 shrink-0" />
                Fichas técnicas e base de ingredientes
              </div>
              <div className="flex items-center gap-2.5 text-sm text-zinc-700">
                <ShieldCheck size={16} className="text-zinc-400 shrink-0" />
                Sessão unificada na Teia de Sabores
              </div>
            </div>
            <p className="text-xs text-rose-600 mb-3 text-center" data-testid="oauth-error-msg">{error}</p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={handleDeny} disabled={authorizing} data-testid="oauth-deny-btn">
                Cancelar
              </Button>
              <Button className="flex-1 bg-zinc-950 text-white hover:bg-zinc-800" onClick={handleAuthorize} disabled={authorizing} data-testid="oauth-authorize-btn">
                {authorizing ? "Autorizando..." : "Autorizar"}
              </Button>
            </div>
            <p className="text-[11px] text-zinc-400 text-center mt-4">
              Você será redirecionado com segurança para fawkesatlascore.link
            </p>
          </>
        )}
      </div>
    </div>
  );
}
